import { Form, List, Row } from "antd"
import { ActionButtons, IconTextDate, Separator, TitleDescription } from "components/custom"
import { TIME_DATE_FORMAT } from "constants"
import { CLIENT_DETAIL_PATH } from "constants/app-paths"
import dayjs from "dayjs"
import AiSalesPitchGenerator from "features/aiPitchGenerator"
import ApplyDiscountForm from "features/clients/components/applyForDiscountForm/applyForDiscountForm"
import {
  useAddClientContactViewTrackingMutation,
  useGetLeadsQuery,
  useMarkFavoriteUnFavoriteMutation,
} from "features/clients/detail/api"
import { CallLogsDrawer, ClientInfo, ProjectModal, TaskOverviewModal } from "features/shared/components"
import AddToSalesPipeline from "features/shared/components/addToSalesPipeline/addToSalesPipeline"
import ProposalGenerationForm from "features/shared/components/proposalGenerationForm/proposalGenerationForm"
import UserProfileSummary from "features/shared/components/userProfileSummary"
import { classifications } from "features/shared/data"
import { AddTaskModal } from "features/tasks/components"
import ClientProfileForm from "features/tasks/components/clientProfileForm/clientProfileForm"
import TsrDrawer from "features/tsr/components/tsrDrawer"
import { t } from "i18next"
import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { Link } from "react-router-dom"
import { capitalizeFirstLetter, convertToReadableString, isModule, joinBytItems, localisedValue } from "utils"
import { getElapsedTime } from "utils/date"
import { fireEvent } from "utils/gtm/gtmEventHandler"
import { ACTION_TYPES, PAGES, PAGE_SUBSECTIONS } from "utils/gtm/gtmEventsData"
import getPrivilege from "utils/privileges/privileges"
import { AddSalesModal, LeadsFilters, ShareLeadFractionalForm, ShareLeadSaleForm, ShareLeadSupportForm } from ".."
import {
  Button,
  DataTable,
  DateFormat,
  FLagWithText,
  Flex,
  Icon,
  Space,
  Tag,
  Text,
  Tooltip,
} from "../../../../components/common"
import BuyRentModal from "../buyRentModal/buyRentModal"
import CloseLostForm from "../closeLostForm/closeLostForm"
import ShareWithMortgage from "../mortgageLoanCalculator"
import SellRentModal from "../sellRentModal/sellRentModal"
import ViewSharingDetail from "../viewSharingDetail/viewSharingDetail"
import WatchListForm from "../watchListForm/watchListForm"
import FlaggedLeads from "../flaggedLeads/flaggedLeads"
import LeadCommentsPopover from "./components/leadCommentsPopOver"
import { AddSalesContextProvider } from "../addSalesModal/addSalesContext/addSalesContext"

import getActionButtons from "./getActionButtons"
import WhatsAppChat from "features/clients/components/whatsAppChat/whatsAppChat"
import SalesPlaybookDrawer from "../salesPlaybook"
import theme from "utils/themeVars"
import AddLeadReviewDrawer from "../addReviewLead/addReviewLead"
import FractionalSaleModal from "../fractionalSaleModal"
import AddInvestmentWealthPipelineModal from "features/wealthInvestmentPipeline"

const leadsColumn = (width = 186, isListing, isRecommended, isBProperty, isAIRecommended, showConcerns) => [
  ...(isListing ? [{ title: "", dataIndex: "favorite", width: 40 }] : []),
  { title: t("Lead Id"), dataIndex: "id", key: "id_col", component: "IconText" },
  ...(isListing ? [{ title: t("Client"), dataIndex: "client", key: "client" }] : []),
  { title: t("Last Task"), dataIndex: "lastTask", key: "lastTask" },
  ...(isListing && isBProperty ? [{ title: t("Stage"), dataIndex: "stage" }] : []),
  { title: t("Interest"), dataIndex: "interest" },
  ...(isRecommended
    ? [{ title: t("Recommended"), dataIndex: "recommended" }]
    : isAIRecommended
      ? [{ title: t("Conversion %"), dataIndex: "conversion" }]
      : showConcerns
        ? [{ title: t("Concerns"), dataIndex: "concerns" }]
        : [{ title: t("Source"), dataIndex: "source" }]),
  ...(isListing ? [{ title: t("Allocated To"), dataIndex: "allocated_to" }] : []),
  { title: t("Actions"), dataIndex: "actions", width: isListing ? 240 : width, className: "text-right" },
]

const LeadsTable = ({
  client: cli,
  params,
  filters = false,
  mainClass,
  filterClass,
  children,
  showPagination = true,
  setLeadCount = () => {},
  showAnimation = true,
  actionWidth,
  from = {},
  loadingComponentStyle,
  getSelectedRows,
  selectedRows,
  highlightedId,
  leadsFrom,
  stickyOffset = 0,
  limitResults = 10,
  skeletonRowCount,
}) => {
  const { t, i18n } = useTranslation()

  const [form] = Form.useForm()
  const clientId = cli?.id
  const isListing = leadsFrom === "listing" ? true : false
  const shareLeadSaleFormRef = useRef()
  const shareLeadSupportFormRef = useRef()
  const addSalesFormRef = useRef()
  const addReviewLeadFormRef = useRef()
  const addApplyForDiscountFormRef = useRef()
  const taskLogRef = useRef()
  const addTaskRef = useRef()
  const proposalGenerationRef = useRef()
  const projectRef = useRef()
  const closeLostRef = useRef()
  const callDrawerRef = useRef()
  const secondaryBuyerRef = useRef()
  const secondarySellerRef = useRef()
  const aiSaleGenRef = useRef()
  const clientProfileRef = useRef()
  const addToSalesPipeline = useRef()
  const addInvestmentWealthPipelineRef = useRef()
  const mortgageAssessmentRef = useRef()
  const filterRef = useRef()
  const drawerRef = useRef()
  const commentsModalRef = useRef()
  const watchListRef = useRef()
  const whatsappChatDrawerRef = useRef()
  const userPrivileges = useSelector(state => state.app.privileges)
  const isUserBlocked = useSelector(state => state?.app?.user?.info?.isBlocked)
  const salesPlayBookDrawerRef = useRef()
  const fractionalSalesRef = useRef()
  const shareLeadFractionalFormRef = useRef()
  const isSharedLeadsPage = isModule("shared-leads")

  const formRefs = {
    shareLeadSaleFormRef: shareLeadSaleFormRef,
    shareLeadSupportFormRef: shareLeadSupportFormRef,
    addSaleFormRef: addSalesFormRef,
    addReviewLeadFormRef: addReviewLeadFormRef,
    addApplyForDiscountFormRef: addApplyForDiscountFormRef,
    taskLogRef: taskLogRef,
    addTaskRef: addTaskRef,
    proposalGenerationRef: proposalGenerationRef,
    closeLostRef: closeLostRef,
    callDrawerRef: callDrawerRef,
    aiSaleGenRef: aiSaleGenRef,
    clientProfileRef: clientProfileRef,
    addToSalesPipeline: addToSalesPipeline,
    addInvestmentWealthPipelineRef: addInvestmentWealthPipelineRef,
    mortgageAssessmentRef: mortgageAssessmentRef,
    watchListRef: watchListRef,
    whatsappChatDrawerRef: whatsappChatDrawerRef,
    fractionalSalesRef: fractionalSalesRef,
    shareLeadFractionalFormRef: shareLeadFractionalFormRef,
  }

  const [lead, setLead] = useState()
  const [selectedInventoryId, setSelectedInventoryId] = useState({
    listingId: null,
    leadId: null,
    clientName: null,
  })

  const [filter, setFilter] = useState({ sortBy: "id", sortOrder: "desc", pageLimit: limitResults })
  const [tabKey, setTabKey] = useState("")
  const {
    data: leads,
    isFetching,
    error,
    refetch,
  } = useGetLeadsQuery({ ...filter, ...params, ...(clientId && { clientId }) }, { refetchOnMountOrArgChange: true })
  const [markFavoriteUnfavorite, { isError: markFavIsError, error: markFavError }] = useMarkFavoriteUnFavoriteMutation()
  const [addClientContactView] = useAddClientContactViewTrackingMutation()

  const user = useSelector(state => state.app.user?.info)
  const client = leadsFrom === "listing" ? lead?.Client : cli

  useEffect(() => {
    !isFetching && leads && setLeadCount(leads?.count)
  }, [leads, isFetching])

  useEffect(() => {
    if (drawerRef?.current?.isDrawerOpen() && !isFetching) {
      if (!leads?.data?.length) drawerRef?.current?.closeDrawer()
      else {
        setLead({ lead: leads?.data[0], index: 0 })
        drawerRef?.current?.showLeadDetail(leads?.data)
      }
    }
  }, [leads, isFetching])

  const onSort = obj => {
    setFilter(prev => ({ ...prev, sortBy: obj.columnName, sortDirection: obj.sortOrder }))
  }

  const onActionClick = (componentRef, lead, index, actionKey) => {
    if (isSharedLeadsPage && ["tasks", "callLogs", "leadReviews", "addTask"].includes(actionKey)) {
      setLead({ lead: leads?.data?.[index], index: index })
      if (actionKey === "addTask") {
        drawerRef.current.showLeadDetail(leads?.data, null, true)
      } else {
        drawerRef.current.showLeadDetail(leads?.data, actionKey, false)
      }
    } else {
      setLead(lead)
      setSelectedInventoryId({
        listingId: lead?.id,
        leadId: lead?.id,
        clientName: lead?.name,
      })
      componentRef?.current?.setVisible(
        actionKey === "observationMeeting"
          ? { preSelectMeeting: true }
          : actionKey === "leadReview"
            ? { preSelectLead: true, lead }
            : true
      )
    }
  }

  const fireFavoriteEvent = (status, clientIds) => {
    fireEvent({
      ...ACTION_TYPES.mark_lead_as_favorite,
      page: PAGES.lead_listing_page,
      interacted_from: PAGE_SUBSECTIONS.lead_listing_page,
      client_id: clientIds.join(","),
      status,
    })
  }

  const handleFavorite = async (favorite, clientIds) => {
    const resp = await markFavoriteUnfavorite({ favorite, clientIds })
      .unwrap()
      .catch(e => fireFavoriteEvent(e.status, clientIds))
    if (!markFavIsError) {
      fireFavoriteEvent("200", clientIds)
    }
  }

  const getInterestTitle = e =>
    // TODO: Need to  get privilege for secondary lead actions
    e?.interestedIn == "secondary" ? (
      <div className="text-link pointer">{localisedValue({ key: "name", object: e?.Type })}</div>
    ) : getPrivilege(["can_view_client_project_summary"], e?.privileges) ? (
      <div className="text-link pointer">{localisedValue({ key: "name", object: e.Project })}</div>
    ) : (
      localisedValue({ key: "name", object: e.Project })
    )

  const getInterestDesc = e => {
    const items =
      e?.interestedIn === "secondary"
        ? [localisedValue({ key: "name", object: e.Purpose }), "Secondary"]
        : [
            localisedValue({ key: "name", object: e?.Type }),
            localisedValue({ key: "name", object: e.Purpose }),
            t(capitalizeFirstLetter(localisedValue({ key: "interestedIn", object: e }))),
          ]
    return joinBytItems(items, <Icon icon="GoDotFill" size=".7em" color="#D6D6D6" />)
  }

  const handleSearch = objFilter => {
    setFilter({ ...objFilter })
  }

  const onClickInterest = e => event => {
    // TODO: Need to get privilege for secondary lead actions
    event.stopPropagation()
    if (e?.interestedIn == "secondary") {
      if (e?.leadSide === "demand") {
        secondaryBuyerRef.current.showFor(e)
      } else if (e?.leadSide === "supply") {
        secondarySellerRef.current.showFor(e)
      }
    } else if (getPrivilege(["can_view_client_project_summary"], e?.privileges)) {
      projectRef.current.showFor(e?.Project?.projectId)
      fireEvent({
        ...ACTION_TYPES.project_details_clicked,
        project_id: e?.Project?.projectId,
        client_id: e?.Client?.id,
      })
    }
  }

  const shouldDisplayCommentsIcon = e => {
    //Task doesn't classify as 'new'
    if (!(e?.InternalInquiryStatus?.key === "new" && e?.LatestTask === null)) return false

    //Task has no additional information provided that needs to be displayed
    if (!!!(e?.externalInquiryId && e?.externalApprovalInfo)) return false

    return true
  }

  const getClassificationsList = lead => {
    return [
      [t("Telesales"), classifications()[lead?.tsrClassificationId]],
      [t("Sales"), classifications()[lead?.classificationId]],
    ]
  }

  const tableData = useMemo(
    () =>
      leads?.data?.length
        ? leads?.data.map((e, index) => {
            const appliedPrivileges = isUserBlocked ? userPrivileges : e?.privileges
            const leadClassification = classifications()[e?.tsrClassificationId || e?.classificationId]
            return {
              key: e.id,
              ...(isListing &&
                !isUserBlocked && {
                  favorite: (
                    <Icon
                      icon={
                        e.Client.isFavoriteOf?.length && e.Client?.isFavoriteOf.includes(user?.id)
                          ? "AiFillStar"
                          : "AiOutlineStar"
                      }
                      color="#F5B301"
                      size="22px"
                      style={{ cursor: "pointer" }}
                      tooltipText={
                        e?.Client?.isFavoriteOf?.length && e?.Client?.isFavoriteOf.includes(user?.id)
                          ? t("Click to remove from favorites")
                          : t("Click to add to favorites")
                      }
                      onClick={event => {
                        event?.stopPropagation()
                        handleFavorite(!(e.Client.isFavoriteOf?.length && e.Client?.isFavoriteOf.includes(user?.id)), [
                          e.Client.id,
                        ])
                      }}
                    />
                  ),
                }),
              id: (
                <>
                  <IconTextDate
                    message={
                      isSharedLeadsPage ? (
                        <List
                          size="small"
                          dataSource={getClassificationsList(e)}
                          onClick={e => e.stopPropagation()}
                          renderItem={item => (
                            <List.Item>
                              <>
                                <div style={{ paddingRight: 6 }}>{item[0]}</div>
                                <div style={{ textAlign: "right", color: item[1]?.style.color }}>
                                  {item[1]?.name || "-"}
                                </div>
                              </>
                            </List.Item>
                          )}
                        />
                      ) : (
                        classifications()[e?.classificationId]?.name || t("Classification Not Available")
                      )
                    }
                    iconProps={{
                      color: leadClassification?.style?.color || "#808080",
                      hasBackground: true,
                    }}
                    icon={"MdLocalFireDepartment"}
                    title={e?.id}
                    date={e?.createdAt}
                    customTooltip={t(`Lead Added on`) + ": "}
                    suffixIcon={e?.isShared ? <ViewSharingDetail leadId={e?.id} /> : null}
                    additionalIcon={shouldDisplayCommentsIcon(e) ? <LeadCommentsPopover lead={e} /> : null}
                    flagIcon={e?.isFlagged ? <FlaggedLeads lead={e} redirectToClientReviews={true} /> : null}
                    linkInfo={
                      isListing && getPrivilege("can_view_client_details", appliedPrivileges)
                        ? CLIENT_DETAIL_PATH.replace(":clientId", e?.Client?.id)
                        : false
                    }
                    from={PAGE_SUBSECTIONS.lead_listing_page}
                  />
                </>
              ),
              ...(isListing && {
                client: (
                  <>
                    <TitleDescription
                      title={
                        <Row>
                          {getPrivilege("can_view_client_details", e?.privileges) ? (
                            <Link
                              to={CLIENT_DETAIL_PATH.replace(":clientId", e.Client?.id)}
                              state={{ hasPath: true, from: PAGE_SUBSECTIONS.lead_listing_page }}
                              className="text-link"
                            >
                              {localisedValue({ key: "name", object: e.Client })}
                            </Link>
                          ) : (
                            <div>{localisedValue({ key: "name", object: e.Client })}</div>
                          )}
                        </Row>
                      }
                      icon={getPrivilege("can_search_clients", userPrivileges) ? "BsStars" : null}
                      iconOnClick={icon => {
                        getPrivilege("can_search_clients", userPrivileges) &&
                          salesPlayBookDrawerRef.current?.openDrawer({ client: e?.Client })
                        icon?.stopPropagation()
                      }}
                      iconProps={{
                        iconColor: theme["accent-color"],
                        hasBackground: true,
                        size: "1em",
                        className: "pointer",
                        tooltipText: t("AI Sales Playbook"),
                      }}
                      description={
                        <FLagWithText
                          title={localisedValue({ key: "name", object: e.Client?.Country })}
                          countryCode={e.Client.Country?.locCode}
                          text={
                            localisedValue({ key: "name", object: client?.City }) ||
                            localisedValue({ key: "name", object: client?.Country })
                          }
                          infoText={
                            <div className="gray-800 fs14">{`${e.Client.inquiryCount} ${
                              e.Client.inquiryCount > 1 ? t("Leads") : t("Lead")
                            }`}</div>
                          }
                        />
                      }
                    />
                  </>
                ),
              }),
              lastTask:
                e?.lastTask === null ? (
                  <>
                    <Button
                      className="pi-0"
                      type="link"
                      icon="MdAddTask"
                      text={t("Add Task")}
                      onClick={event => {
                        fireEvent({ ...ACTION_TYPES.lead_add_task_button, ...from, client_id: clientId, lead_id: e.id })
                        onActionClick(addTaskRef, e, index, "addTask")
                        event?.stopPropagation()
                      }}
                      disabled={!getPrivilege("can_add_task_on_lead", appliedPrivileges)}
                    />
                    <Tooltip title={`${t("Unattended since")} ${getElapsedTime(e?.createdAt).slice(0, -4)}`}>
                      <span style={{ marginLeft: 5, position: "relative", top: "-2px" }}>
                        <Icon icon="CiWarning" size="18px" color="#7c7c7c" />
                      </span>
                    </Tooltip>
                  </>
                ) : (
                  <TitleDescription
                    title={localisedValue({ key: "name", object: e.InternalInquiryStatus })}
                    description={
                      isSharedLeadsPage ? (
                        <Tooltip title={dayjs(e?.lastTaskDate).format(TIME_DATE_FORMAT)}>
                          {getElapsedTime(e?.lastTaskDate)}
                        </Tooltip>
                      ) : (
                        <DateFormat date={e?.lastTaskDate} customTooltip={t("Task Completion Date") + ": "} />
                      )
                    }
                    key={`lastTask - ${e?.id}`}
                  />
                ),
              ...(isListing &&
                user?.AgencyConfiguration?.Platform?.platformKey === "bproperty" && {
                  stage: (
                    <TitleDescription
                      title={
                        <Text level={4}> {localisedValue({ key: "stageName", object: e?.InternalInquiryStage })}</Text>
                      }
                      description={
                        <Text type="secondaryLight">
                          {localisedValue({ key: "substageName", object: e?.InternalInquiryStage })}
                        </Text>
                      }
                    />
                  ),
                }),
              interest:
                e?.interestedIn === "mortgage" ? (
                  <Text>{t("Bank Loan")}</Text>
                ) : e?.interestedIn === "investment" && !e?.Project?.name ? (
                  <Text>{t("Investment")}</Text>
                ) : e?.interestedIn === "fractional" ? (
                  <Text>{t("Fractional")}</Text>
                ) : (
                  <TitleDescription
                    title={getInterestTitle(e)}
                    gapSpace={4}
                    onClick={event => onClickInterest(e)(event)}
                    description={getInterestDesc(e)}
                    titleClassName="text-link"
                    key={`interest - ${e.id}`}
                  />
                ),
              source: (
                <TitleDescription
                  title={
                    <Flex gap="4px">
                      {capitalizeFirstLetter(localisedValue({ key: "referralType", object: e?.Client })) || "-"}
                      {!!getPrivilege("can_view_lead_source_data", userPrivileges) && <ClientInfo client={e} />}
                    </Flex>
                  }
                  description={convertToReadableString(localisedValue({ key: "name", object: e.source }))}
                />
              ),
              recommended: e.recommended,
              ...(isListing && {
                allocated_to: e.User && (
                  <div>
                    <div>
                      {localisedValue({ key: "name", object: e.User })}&nbsp;
                      <UserProfileSummary staffId={e?.User?.id} />
                    </div>
                    <div className="gray-800 fs14">
                      {joinBytItems(
                        [
                          localisedValue({ key: "short", object: e.User?.UserDesignation }),
                          localisedValue({ key: "name", object: e?.User?.City }),
                        ],
                        <Separator />
                      )}
                    </div>
                  </div>
                ),
              }),
              conversion: (
                <Text
                  className="text-link"
                  onClick={() => {
                    setTabKey("conversionProbability")
                    onActionClick(taskLogRef, e)
                    taskLogRef.current.setVisible(true)
                  }}
                >
                  {e?.Client?.aiClosureProbability}%
                </Text>
              ),
              concerns: (
                <TitleDescription
                  title={localisedValue({ key: "clientInterest", object: e?.concerns })}
                  description={
                    <Space size={4} wrap>
                      {e?.concerns?.areasOfConcern?.[0]}
                      {e?.concerns?.areasOfConcern?.length > 1 && (
                        <span
                          style={{
                            color: theme["accent-color"],
                            cursor: "pointer",
                          }}
                        >
                          <Tooltip placement="top" title={e?.concerns?.areasOfConcern.slice(1).join(",")}>
                            +{e?.concerns?.areasOfConcern?.length - 1}
                          </Tooltip>
                        </span>
                      )}
                    </Space>
                  }
                />
              ),
              // nextTask: (
              //   <TitleDescription
              //     title={localisedValue({ key: "statusTitleTodo", object: e.NextStatusWanted })}
              //     description={e?.nextStatusDeadline && <DateFormat date={e.nextStatusDeadline} />}
              //     key={`source - ${e.id}`}
              //   />
              // ),
              actions: (
                <ActionButtons
                  list={getActionButtons({
                    lead: e,
                    isListing,
                    client,
                    formRefs,
                    onActionClick,
                    user,
                    from,
                    showAnimation,
                    userPrivileges,
                    addClientContactView,
                    index,
                    privileges: appliedPrivileges,
                  })}
                />
              ),
            }
          })
        : [],
    [leads?.data, user, client]
  )

  return (
    <>
      {filters && (
        <LeadsFilters filterObject={filter} filterClass={filterClass} onSearch={handleSearch} onSort={onSort} />
      )}
      <DataTable
        tableLayout="auto"
        pagination={showPagination ? { totalCount: leads?.count, pageOptions: [10, 25, 50, 300] } : {}}
        paginationOnBottom={true}
        paginationOnTop={false}
        loadingText={<></>}
        columns={leadsColumn(
          actionWidth,
          isListing,
          false,
          user?.AgencyConfiguration?.Platform?.platformKey === "bproperty",
          params?.category === "ai_recommended",
          params?.category === "audit_concerns"
        )}
        data={tableData || []}
        loading={isFetching}
        error={error}
        onErrorRetry={refetch}
        mainClass={mainClass}
        stickyOffset={stickyOffset}
        rowClassName={(_record, index) =>
          leads?.data?.[index]?.hasActiveSale
            ? "table-active_sale-row-bg"
            : leads?.data?.[index]?.id == highlightedId
              ? "table-highlight-row-bg"
              : " "
        }
        skeletonType={isListing ? "large" : "small"}
        loadingComponentStyle={loadingComponentStyle}
        rowSelection={
          isListing &&
          !isUserBlocked && {
            onChange: rows => {
              const transformData = leads.data.filter(item => {
                return rows.includes(item.id)
              })
              getSelectedRows(transformData)
            },
            columnWidth: 70,
            selectedRowKeys: selectedRows.map(item => item.id),
          }
        }
        onRow={(record, rowIndex) => {
          return {
            onClick: _event => {
              if (isSharedLeadsPage) {
                setLead({ lead: leads?.data?.[rowIndex], index: rowIndex })
                drawerRef.current.showLeadDetail(leads?.data)
              }
            },
            className: lead?.index === rowIndex ? "ant-table-row-selected" : "pointer",
          }
        }}
        skeletonRowCount={skeletonRowCount}
      />
      {children && children({ leadCount: leads?.count })}
      <ProjectModal ref={projectRef} />
      <BuyRentModal ref={secondaryBuyerRef} />
      <SellRentModal ref={secondarySellerRef} />
      <ShareLeadSaleForm leads={[lead]} client={client} formRef={shareLeadSaleFormRef} from={from} />
      <ShareLeadSupportForm leads={[lead]} client={client} formRef={shareLeadSupportFormRef} from={from} />
      <AddSalesContextProvider>
        <AddSalesModal lead={lead} client={client} formRef={addSalesFormRef} from={from} />
      </AddSalesContextProvider>
      <FractionalSaleModal lead={lead} client={client} formRef={fractionalSalesRef} from={from} />
      <AddLeadReviewDrawer lead={lead} client={client} ref={addReviewLeadFormRef} />
      <ApplyDiscountForm lead={lead} client={client} formRef={addApplyForDiscountFormRef} from={from} />
      <AddTaskModal lead={lead} client={client} formRef={addTaskRef} from={from} />
      <ProposalGenerationForm
        formRef={proposalGenerationRef}
        from={{ name: "lead", listingId: lead?.id, client: client }}
      />
      <TaskOverviewModal
        ref={taskLogRef}
        client={client}
        lead={lead}
        taskTableProps={{ from: from, lead: { inquiryIds: lead?.id?.toString() }, isEditable: true }}
        getClientLogs
        tabKey={tabKey}
      />
      <ClientProfileForm
        formRef={clientProfileRef}
        client={client}
        allocatedTo={lead?.User?.name}
        onSuccess={() => clientProfileRef.current?.setVisible(false)}
      />
      <CloseLostForm formRef={closeLostRef} lead={lead} />
      <CallLogsDrawer ref={callDrawerRef} />
      <AiSalesPitchGenerator ref={aiSaleGenRef} />
      <AddToSalesPipeline ref={addToSalesPipeline} lead={lead} client={client} allocatedTo={lead?.User?.name} />
      <AddInvestmentWealthPipelineModal ref={addInvestmentWealthPipelineRef} lead={lead} client={client} />
      <ShareWithMortgage ref={mortgageAssessmentRef} lead={[lead]} />
      <ShareLeadFractionalForm leads={[lead]} client={client} formRef={shareLeadFractionalFormRef} from={from} />
      <TsrDrawer
        lead={lead}
        setLead={lead => setLead({ ...lead })}
        refs={drawerRef}
        totalLeads={leads?.count}
        params={params}
        page={params?.page || 0}
        pageLimit={params?.pageLimit || 10}
      />
      <WatchListForm lead={lead} formRef={watchListRef} />
      <WhatsAppChat
        ref={whatsappChatDrawerRef}
        client={{ ...lead?.Client, User: { ...lead?.User, userId: lead?.User?.id } }}
        sharingInfo={lead?.InternalInquirySharings?.map(e => e.shareWith) || []}
      />
      <SalesPlaybookDrawer refs={salesPlayBookDrawerRef} />
    </>
  )
}

export default LeadsTable
